import { BACKEND_URL } from '../CoreCpts/lib/urls';

/**
 * Failure-only reporting for the drafts system.
 *
 * This is deliberately NOT telemetry. `/log-event`
 * (`srcy/flasky/routes/base.py`) writes one row into the shared audit-log
 * container per call and its own docstring asks for at most one event per
 * page view, so nothing on a success path may call this — no periodic
 * events, no per-interaction events, no "draft saved" events. A periodic
 * count of how many drafts a device holds is wanted, but it is telemetry and
 * needs its OWN helper; routing it through here would make the sentence above
 * false.
 *
 * `kind` NAMES A CATEGORY OF EVENT, NOT A SUBSYSTEM. The server's allowlist
 * (`CLIENT_LOG_KINDS` in
 * `srcy/flasky/routes/funcs_used_by_routes/client_log_event.py`) is exactly
 * `telemetry` and `error`, kept small on purpose, and the sanitizer rejects
 * anything else with a 400. Everything reported from here is a failure, so
 * the category is `error` and the stored title reads
 * `client:error:<event_title>`. This module used to send `kind: 'autosave'` —
 * the subsystem rather than the category — so from the day it was written
 * every event it sent was refused and nothing reached the log container, and
 * because the post is fire-and-forget nothing surfaced. Do not "restore" a
 * subsystem-shaped kind; put the subsystem in `event_title`.
 *
 * HIPAA: never pass draft content, field values, note ids, folder ids or
 * pathnames. Counts and verdicts only.
 */
export const logAutosaveHazardEvent = (
  eventTitle: string,
  eventDetails: string,
  eventDetailsData: Record<string, number | string | boolean>
): void => {
  // Fire and forget. A failure to report a failure must never surface to the
  // worker or break the form.
  void fetch(`${BACKEND_URL}/log-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      kind: 'error',
      event_title: eventTitle,
      event_details: eventDetails,
      // Keep every volatile value out of here: event_details_data IS the
      // dedup identity, so a timestamp or id in it silently defeats dedupe.
      event_details_data: eventDetailsData,
      dedupe: true,
      client_ts: Date.now(),
    }),
  })
    .then((res) => {
      if (res.ok) return;
      // `fetch` resolves on 4xx and 5xx, so with only a `.catch()` a REFUSED
      // report is indistinguishable from a delivered one. That is exactly how
      // this module sent a `kind` the server's allowlist rejects for five
      // days: every event came back 400, nothing reached the log container,
      // and nothing said so. The instance is fixed above; this catches the
      // class. It is worth a line of console noise here in particular because
      // the subsystem being reported on is otherwise invisible — the drafts
      // these events are about live only in one device's IndexedDB, with no
      // server copy anyone can go back and look at.
      //
      // Still best-effort, exactly like the post: a warn and nothing else. No
      // throw, no rejection, nothing that reaches the worker or the form.
      console.warn(
        `[drafts] hazard report refused (HTTP ${res.status}): ${eventTitle}`
      );
    })
    .catch(() => {
      /* reporting is best-effort */
    });
};

/**
 * Event titles already reported by `logAutosaveHazardEventOnce` in this page
 * session. Module scope, so it lives exactly as long as the loaded bundle: a
 * reload is a new session and reports again, which is what we want — the
 * question "is this device still wedged?" should be re-answerable.
 *
 * Reset it in a test with `jest.resetModules()` (or `jest.isolateModules`)
 * rather than an exported clear function: a clear function is reachable from
 * app code and the latch only works if nothing app-side can lift it.
 */
const alreadyReportedTitles = new Set<string>();

/**
 * ATTEMPT a hazard report AT MOST ONCE PER PAGE SESSION, keyed on
 * `eventTitle`. Attempt, not report: the latch below is marked before the POST
 * is handed to `logAutosaveHazardEvent`, so if that request is dropped or
 * refused the session has spent its one report and will not try again. That is
 * the deliberate trade — see the last paragraph.
 *
 * For hazards that recur on a timer rather than once per incident. The case
 * this exists for is `draft-persist-failed` in `AutosaveContext`'s persist
 * effect: that effect is driven by the 500ms `debouncedUpdateAutosave`, so a
 * device whose storage is wedged (quota full, IDB blocked) fails again on
 * every keystroke-debounce and would POST for the rest of the session. The
 * server's `dedupe: true` is not a substitute — it suppresses the WRITE inside
 * a 60s window and only within one gunicorn worker, so the requests still go,
 * the shared audit-log container still collects a row a minute per worker, and
 * `/log-event`'s own docstring asks for at most one event per page view.
 *
 * A streak is one fact, so attempt the first of it and stop. Use the unlatched
 * `logAutosaveHazardEvent` for hazards that are naturally one-per-incident
 * (`indexeddb-unavailable`, `device-draft-wipe-failed`,
 * `superseded-draft-offered`) — latching those would hide the second incident.
 *
 * Deliberately keyed on the title alone and not on `eventDetailsData`: the
 * mode and quota flags of a wedged device can flap (an IDB failure can push
 * the app into localStorage mode), and keying on them would let one streak
 * report several times, which is the thing being prevented.
 *
 * And deliberately marked BEFORE the POST rather than after a 2xx. Marking on
 * success would mean that while the server is refusing or the network is down
 * — i.e. during the very failure streak this latch exists for — every
 * debounce tick would try again, which is the per-tick flood the latch
 * prevents. The cost is the one named above: a dropped or refused first
 * attempt loses the session's only report. The `console.warn` in
 * `logAutosaveHazardEvent` is what makes that loss visible locally.
 */
export const logAutosaveHazardEventOnce = (
  eventTitle: string,
  eventDetails: string,
  eventDetailsData: Record<string, number | string | boolean>
): void => {
  if (alreadyReportedTitles.has(eventTitle)) return;
  alreadyReportedTitles.add(eventTitle);
  logAutosaveHazardEvent(eventTitle, eventDetails, eventDetailsData);
};
